import { z } from 'zod';

/**
 * Defines all possible top-level CMS component types.
 * These types can be used directly in page layouts.
 */
export const CMSComponentTypeEnum = z.enum([
  'card',
  'columns',
  'banner',
  'tabs',
  'text',
  'text_advanced',
  'plan_cards',
  'article_card',
  'call_to_action',
  'image',
  'video',
  'cta',
  'queue_count',
  'inline_text_image',
  'placeholder',
  'form',
]);

/**
 * Defines component types that can only appear nested within other components.
 * These types cannot be used as top-level components.
 */
export const CMSNestedComponentTypeEnum = z.enum([
  'address_search',
  'content',
  'trustpilot_widget',
  'trustpilot_carousel',
  'faqs',
  'average_activation',
  'rich_text',
]);

export const CMSCardTheme = z.enum([
  // TODO: comment to remove this after the migration(probably 1 week)
  // will be depreacted
  'no-fill',
  'light-green',
  'mid-green',
  'dark-green',

  // will not be deprecated
  'grey',
  'light-grey',

  // new ones
  'transparent',
  'white',
  'deep-teal-50',
  'deep-teal-100',
  'deep-teal-500',
  'deep-teal-700',
  'deep-teal-800',
  'lime-50',
  'lime-100',
  'lime-300',
  'spearmint-100',
  'green-50',
  'green-100',
  'tan-100',

  // new brand updates
  'black',
  'light-grey-200',
  'warp-green-50',
  'warp-green-100',
  'custom-warp-green',
]);

export type CMSCardTheme = z.infer<typeof CMSCardTheme>;

export const CMSCardImagePosition = z.enum(['start', 'end']);

export type CMSCardImagePosition = z.infer<typeof CMSCardImagePosition>;

/**
 * Represents a `CMSCard` component before lazy initialization
 **/

export const CTATargetOption = z.enum(['_blank', 'none', 'drawer', 'chatbot']);
const CTAGtmMeta = z.unknown();
export type CTAGtmMetaType = z.infer<typeof CTAGtmMeta>;
const CMSCardTargetSchema = z.object({
  key: CTATargetOption,
});

export type CTATarget = z.infer<typeof CMSCardTargetSchema>['key'];

export const PartialPageSlugSchema = z.enum([
  'download-app',
  'unlimited-international',
  'esim-compatibility',
]);

export type PartialPageSlug = z.infer<typeof PartialPageSlugSchema>;

export const CMSDrawerContentSchema = z.object({
  key: PartialPageSlugSchema.nullable(),
});

const CMSCardLinkTargetSchema = z
  .object({
    key: z.enum(['_blank', 'none']).nullable(),
  })
  .nullable();

const CMSCardStyleSchema = z.object({
  key: z.enum(['primary', 'secondary', 'link']),
});

export const CTAButtonPosition = z.enum(['left', 'center', 'right', 'centre']);
export type CTAButtonPosition = z.infer<typeof CTAButtonPosition>;
const CMSCardCTAButtonPositionSchema = z.object({
  key: CTAButtonPosition,
});

export const BaseCMSCardSchema = z.object({
  type: z.literal(CMSComponentTypeEnum.Enum.card),
  id: z.string(),
  theme: z.object({
    key: CMSCardTheme,
  }),
  layout: z.object({
    key: z.enum(['vertical', 'horizontal', 'responsive']),
  }),
  heading: z.string().nullable(), //FIXME: CONFIRM IF THIS IS NULLABLE OR NOT.
  icon_position: z.object({
    key: z.enum(['block', 'inline']),
  }),
  icon_style: z.object({
    key: z.enum(['solid', 'outline']),
  }),
  feature_image: z.object({
    remove_padding: z.boolean(),
    image_position: z.object({
      key: CMSCardImagePosition,
    }),
    feature_image: z
      .object({
        url: z.string().nullable(),
        alt: z.string().nullable(),
      })
      .nullable(),
  }),
  icon_text: z.string(),
  cta: z.object({
    title: z.string().nullable(),
    icon_text: z.string(),
    icon_style: z.object({
      key: z.enum(['solid', 'outline']),
    }),
    url: z.string(),
    style: CMSCardStyleSchema,
    target: CMSCardTargetSchema,
    button_position: CMSCardCTAButtonPositionSchema,
    drawer_content: CMSDrawerContentSchema,
    enable_gtm: z.boolean(),
    gtmCta: CTAGtmMeta,
  }),
  show_image_options: z.boolean(),
  show_cta_options: z.boolean(),
  card_link: z.string().nullable(),
  card_link_target: CMSCardLinkTargetSchema,
  card_link_enable_gtm: z.boolean(),
  cardLinkGtmCta: CTAGtmMeta,
  item_order: z.number().optional(),
  with_animation: z.boolean(),
});

/**
 * Represents a `CMSColumns` component before lazy initialization
 **/
const BaseCMSColumnsSchema = z.object({
  type: z.literal(CMSComponentTypeEnum.Enum.columns),
  id: z.string(),
  columns_type: z.object({
    key: z.enum(['standard', 'carousel']),
  }),
  max_per_row: z.number().positive(),
  show_nav_arrows: z.boolean(),
  show_nav_dots: z.boolean(),
  overflow_behaviour: z.object({
    key: z.enum(['overflow', 'crop']),
  }),
});

export const CMSBannerTheme = z.enum([
  'dark',
  'light',
  'grey',

  // NEW BRAND UPDATES THEME
  'white',
  'black',
  'light-grey-50',
  'light-grey-100',
  'light-grey-200',
  'warp-green-50',
]);

export type CMSBannerTheme = z.infer<typeof CMSBannerTheme>;

/**
 * Represents a `CMSBanner` component before lazy initialization
 **/
const BaseCMSBannerSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.banner),
  theme: z.object({
    key: CMSBannerTheme,
  }),
  // TODO: Add types properly instead of using z.any()
  background_image: z
    .object({
      alt: z.string().nullable(),
      url: z.string().nullable(),
    })
    .nullable(),
  background_position: z.any(), //FIXME: Add String type bg-bottom bg-center bg-left bg-left-bottom bg-left-top bg-right bg-right-bottom bg-right-top bg-top
});

/**
 * Represents a `CMSTabs` component before lazy initialization
 **/
const BaseCMSTabsSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.tabs),
});

/**
 * Represents a `CMSTab` component before lazy initialization
 **/
const BaseCMSTabSchema = z.object({
  id: z.string(),
  type: z.literal('tab'),
  tab_heading: z.string(),
});

/**
 * Represents a `CMSText` component before lazy initialization
 **/
const BaseCMSTextSchema = z.object({
  type: z.literal(CMSComponentTypeEnum.Enum.text),
});

/**
 * Represents a `CMSTextAdvanced` component before lazy initialization
 **/
const BaseCMSTextAdvancedSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.text_advanced),
});

/**
 * Represents a `CMSPlanCards` component before lazy initialization
 **/
const BaseCMSPlanCardsSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.plan_cards),
});

/**
 * Represents a `CMSArticleCard` component before lazy initialization
 **/
const BaseCMSArticleCardSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.article_card),
});

/**
 * Represents a `CMSAddressSearch` placeholder component before lazy initialization
 **/
const BaseAddressSearchSchema = z.object({
  id: z.string(),
  type: z.literal(CMSNestedComponentTypeEnum.Enum.address_search),
});

//TODO: We need to remove this implementation or find out why is this CMS component for
const BaseCMSContentSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.content),
  id: z.string(),
  enabled: z.boolean().optional(),
  // TODO: Type the items array properly instead of using z.any()
  items: z.array(z.any()).optional(),
});

const BaseCMSTrustpilotWidgetSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.trustpilot_widget),
  id: z.string(),
});

const BaseCMSTrustpilotCarouselSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.trustpilot_carousel),
  id: z.string(),
  trustpilot_carousel: z.null(),
});

const BaseCMSAverageActivationSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.average_activation),
  id: z.string(),
  activation_timer: z.string(),
});

const BaseCMSRichTextSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.rich_text),
  id: z.string(),
});

const BaseCMSFaqsPartialSchema = z.object({
  type: z.literal(CMSNestedComponentTypeEnum.Enum.faqs),
  id: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
    })
  ),
});

const BaseCMSFaqItemSchema = z.object({
  id: z.string(),
  title: z.string(),
});

/**
 * Represents a `CMSCta` component before lazy initialization
 **/
const BaseCMSCallToActionSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.call_to_action),
});

const BaseCMSCTASchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.cta),
});

/**
 * Represents a `CMSImage` component before lazy initialization
 **/
const BaseCMSImageSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.image),
});

/**
 * Represents a `CMSVideo` component before lazy initialization
 **/
const BaseCMSVideoSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.video),
});

/**
 * Represents a `CMSQueueCount` component
 **/
const BaseCMSQueueCountSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.queue_count),
});

/**
 * Represents a `CMSInlineTextImage` component
 **/
const BaseCMSInlineTextImageSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.inline_text_image),
});

/**
 * Represents a `CMSPlaceHolder` component
 **/
const BaseCMSPlaceholderSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.placeholder),
});

/**
 * Represents a `CMSPlaceHolder` component
 **/
const BaseCMSFormSchema = z.object({
  id: z.string(),
  type: z.literal(CMSComponentTypeEnum.Enum.form),
});

/**
 * Represents a Zod schema for a `CMS Card component`
 **/
const CMSCardSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSCardSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()),
  });

/**
 * Represents the entire `CMSCard type`
 **/
export type CMSCard = z.infer<typeof BaseCMSCardSchema> & {
  content: CMSNestedComponent[];
};

/**
 * Represents a Zod schema for `CMSBanner component`
 **/
export const CMSBannerSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSBannerSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()),
  });

/**
 * Represents the entire `CMSBanner type`
 **/
export type CMSBanner = z.infer<typeof BaseCMSBannerSchema> & {
  content: CMSNestedComponent[];
};

/**
 * Represents a Zod schema for `CMSTab component`
 **/
export const CMSTabSchema = BaseCMSTabSchema.extend({
  content: z.lazy(() => CMSNestedComponentSchema.array()),
});

/**
 * Represents the entire `CMSTab type`
 **/
export type CMSTab = z.infer<typeof BaseCMSTabSchema> & {
  content: CMSNestedComponent[];
};

/**
 * Represents a Zod schema for `CMSTabs component`
 **/
export const CMSTabsSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSTabsSchema.extend({
    items: z.array(CMSTabSchema),
  });

/**
 * Represents the entire `CMSTabs type`
 **/
export type CMSTabs = z.infer<typeof BaseCMSTabsSchema> & {
  items: CMSTab[];
};

/**
 * Represents a `CMSText` component with two mutually exclusive variants:
 *
 * 1. Text Variant:
 *    - Has `id` and `text` properties
 *    - Cannot have `content`
 *    Example: { type: 'text', id: '123', text: 'Hello' }
 *
 * 2. Content Variant:
 *    - Has `content` array of nested components
 *    - Cannot have `id` or `text`
 *    Example: { type: 'text', content: [...nestedComponents] }
 **/
export const CMSTextSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSTextSchema.extend({
    // This is type-safe even when we're using optional fn
    content: z.lazy(() => CMSNestedComponentSchema.array().optional()),
    id: z.string().optional(),
    text: z.string().optional(),
  });

/**
 * Represents the entire `CMSText type`
 *
 * Internal variants. CMS is using 1 type under the hood, but some properties are mutually exclusive
 *
 * `Variant1` id & text are defined
 *
 * `Variant2` content is defined
 **/
export type CMSText = z.infer<typeof BaseCMSTextSchema> &
  (
    | {
        id: string;
        text: string;
        content?: never;
      }
    | {
        id?: never;
        text?: never;
        content: CMSNestedComponent[];
      }
  );

/**
 * Represents a Zod schema for `CMSTextAdvanced component`
 **/
export const CMSTextAdvancedSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSTextAdvancedSchema.extend({
    items: z.lazy(() => CMSNestedComponentSchema.array()),
    item_order: z.number().optional(),
  });

/**
 * Represents the entire `CMSTextAdvanced type`
 **/
export type CMSTextAdvanced = z.infer<typeof BaseCMSTextAdvancedSchema> & {
  items: CMSNestedComponent[];
  item_order?: number;
};

/**
 * Represents a Zod schema for `CMSAddressSearch` placeholder component
 **/
export const CMSAddressSearchSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseAddressSearchSchema.extend({
    address_search: z.null(),
  });

/**
 * Represents a placeholder type for `CMSAddressSearch` placeholder component
 **/
export type CMSAddressSearch = z.infer<typeof BaseAddressSearchSchema> & {
  address_search: null;
};

/**
 * Represents a Zod schema for `CMSPlanCards component`
 **/
const ProductTypeSchema = z.object({
  key: z.enum(['BROADBAND', 'MOBILE']),
});

const CustomerSegmentschema = z.object({
  key: z.enum(['RESIDENTIAL', 'SMB']).nullable(),
});

export type ProductTypeKeyEnum = z.infer<typeof ProductTypeSchema>['key'];

export type CustomerSegmentKeyEnum = z.infer<
  typeof CustomerSegmentschema
>['key'];

export const CMSPlanCardsSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSPlanCardsSchema.extend({
    product_type: ProductTypeSchema,
    customer_segment: CustomerSegmentschema,
    // FIXME: We're not using these properties at this stage
    min_download_speed: z.nullable(z.any()),
    max_download_speed: z.nullable(z.any()),
    include_btl_plans: z.boolean(),
  });

/**
 * Represents the entire `CMSPlanCards type`
 **/
export type CMSPlanCards = z.infer<typeof BaseCMSPlanCardsSchema> & {
  product_type: z.infer<typeof ProductTypeSchema>;
  customer_segment?: z.infer<typeof CustomerSegmentschema>;

  // FIXME: We're not using these properties at this stage
  min_download_speed: any;
  max_download_speed: any;
  include_btl_plans: boolean;
} & (
    | {
        product_type: {
          key: 'BROADBAND';
        };
        customer_segment: {
          key: CustomerSegmentKeyEnum;
        };
      }
    | {
        product_type: {
          key: 'MOBILE';
        };
        customer_segment: {
          key?: never;
        };
      }
  );

/**
 * Represents a Zod schema for `CMSArticleCard component`
 **/

const ContentSchema = z.object({
  id: z.string(),
});

export const CMSArticleCardSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSArticleCardSchema.extend({
    content: ContentSchema,
  });

export type CMSArticleCard = z.infer<typeof BaseCMSArticleCardSchema> & {
  content: z.infer<typeof ContentSchema>;
};

/**
 * Represents a Zod schema for `CMS Columns component`
 **/
const CMSColumnsSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSColumnsSchema.extend({
    items: z.lazy(() => CMSNestedComponentSchema.array()),
  });

/**
 * Represents the entire `CMSColumn type`
 **/
export type CMSColumn = z.infer<typeof BaseCMSColumnsSchema> & {
  items: CMSNestedComponent[];
};

/**
 * Content Schema
 */
export const CMSContentSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSContentSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()).optional(),
  });

export type CMSContent = z.infer<typeof BaseCMSContentSchema> & {
  content?: CMSNestedComponent[];
};

/**
 * Trustpilot Widget Schema
 */
export const CMSTrustpilotWidgetSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSTrustpilotWidgetSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()).optional(),
  });

export type CMSTrustpilotWidget = z.infer<
  typeof BaseCMSTrustpilotWidgetSchema
> & {
  content?: CMSNestedComponent[];
};

/**
 * Trustpilot Carousel Schema
 */
export const CMSTrustpilotCarouselSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSTrustpilotCarouselSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()).optional(),
  });

export type CMSTrustpilotCarousel = z.infer<
  typeof BaseCMSTrustpilotCarouselSchema
> & {
  content?: CMSNestedComponent[];
};

/**
 * Average Activation Schema
 */
export const CMSAverageActivationSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSAverageActivationSchema.extend({
    content: z.lazy(() => CMSNestedComponentSchema.array()).optional(),
  });

export type CMSAverageActivation = z.infer<
  typeof BaseCMSAverageActivationSchema
> & {
  content?: CMSNestedComponent[];
};

/**
 * RichText Schema
 */
export const CMSRichTextSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSRichTextSchema.extend({
    text_advanced: z.lazy(() => CMSNestedComponentSchema.array()),
  });

export type CMSRichText = z.infer<typeof BaseCMSRichTextSchema> & {
  text_advanced: CMSNestedComponent[];
};

/**
 * FaqItem Schema
 */
export const CMSFaqItemSchema = BaseCMSFaqItemSchema.extend({
  content: z.lazy(() => CMSNestedComponentSchema.array()),
});

export type CMSFaqItem = z.infer<typeof BaseCMSFaqItemSchema> & {
  content: CMSNestedComponent[];
};

export const CMSFaqItemsResponse = z.array(CMSFaqItemSchema);

/**
 * FAQs Partial Schema
 */
export const CMSFaqsPartialSchema = BaseCMSFaqsPartialSchema;

export type CMSFaqsPartial = z.infer<typeof BaseCMSFaqsPartialSchema>;

/**
 * Represents a Zod schema for `CMSCTAButton component`
 **/
const CTATargetSchema = z.object({
  key: CTATargetOption,
});

const CTADrawerContentSchema = z.object({
  key: PartialPageSlugSchema.nullable(),
});

const CTAButtonStyleSchema = z.object({
  key: z.enum(['primary', 'secondary', 'secondary_black', 'link']),
});

const CTAButtonPositionSchema = z.object({
  key: CTAButtonPosition,
});

const CTAIconText = z.string();
const CTAIconStyle = z.object({
  key: z.enum(['solid', 'outline']),
});

export const CMSCallToActionSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSCallToActionSchema.extend({
    title: z.string().nullable(),
    url: z.string(),
    style: CTAButtonStyleSchema,
    target: CTATargetSchema,
    icon_text: CTAIconText,
    icon_style: CTAIconStyle,
    button_position: CTAButtonPositionSchema,
    drawer_content: CTADrawerContentSchema,
    enable_gtm: z.boolean(),
    gtmCta: CTAGtmMeta,
  });

export type CMSCallToAction = z.infer<typeof BaseCMSCallToActionSchema> & {
  title: string | null;
  url: string;
  style: z.infer<typeof CTAButtonStyleSchema>;
  target: z.infer<typeof CTATargetSchema>;
  icon_text: z.infer<typeof CTAIconText>;
  icon_style: z.infer<typeof CTAIconStyle>;
  button_position: z.infer<typeof CTAButtonPositionSchema>;
  drawer_content: z.infer<typeof CTADrawerContentSchema>;
  enable_gtm: boolean;
  gtmCta?: z.infer<typeof CTAGtmMeta>;
};

export const CMSCTASchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSCTASchema.extend({
    title: z.string().nullable(),
    url: z.string(),
    style: CTAButtonStyleSchema,
    target: CTATargetSchema,
    icon_text: CTAIconText,
    icon_style: CTAIconStyle,
    button_position: CTAButtonPositionSchema,
    drawer_content: CTADrawerContentSchema,
    enable_gtm: z.boolean(),
    gtmCta: CTAGtmMeta,
  });

export type CMSCTA = z.infer<typeof BaseCMSCTASchema> & {
  title: string | null;
  url: string;
  style: z.infer<typeof CTAButtonStyleSchema>;
  target: z.infer<typeof CTATargetSchema>;
  icon_text: z.infer<typeof CTAIconText>;
  icon_style: z.infer<typeof CTAIconStyle>;
  button_position: z.infer<typeof CTAButtonPositionSchema>;
  drawer_content: z.infer<typeof CTADrawerContentSchema>;
  enable_gtm: boolean;
  gtmCta?: z.infer<typeof CTAGtmMeta>;
};

/**
 * Represents a Zod schema for `CMSImage component`
 **/

const ImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  alt: z.string().nullable(),
});

export const CMSImageSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSImageSchema.extend({
    image: ImageSchema,
    item_order: z.number().optional(),
  });

export type CMSImage = z.infer<typeof BaseCMSImageSchema> & {
  image: z.infer<typeof ImageSchema>;
  item_order?: number;
};

/**
 * Represents a Zod schema for `CMSVideo component`
 **/

export const CMSVideoSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSVideoSchema.extend({
    video: z.string(),
    item_order: z.number().optional(),
  });

export type CMSVideo = z.infer<typeof BaseCMSVideoSchema> & {
  video: string;
  item_order?: number;
};

/**
 * Represents a Zod schema for `CMSQueueCount component`
 **/

const LabelPositionSchema = z.object({
  key: z.enum(['left', 'right']),
});

const QueueSchema = z.object({
  key: z.enum(['sales', 'tracking', 'support', 'billing']),
});

export const CMSQueueCountSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSQueueCountSchema.extend({
    queue: QueueSchema,
    label: z.string(),
    label_position: LabelPositionSchema,
  });

export type CMSQueueCount = z.infer<typeof BaseCMSQueueCountSchema> & {
  queue: z.infer<typeof QueueSchema>;
  label: string;
  label_position: z.infer<typeof LabelPositionSchema>;
};

/**
 * Represents a Zod schema for `CMSInlineTextImage component`
 **/

const InlineTextImageSchema = z.array(
  z.object({
    url: z.string(),
    alt: z.string().nullable(),
  })
);

const ImagePositionSchema = z.object({
  key: z.enum(['start', 'end']),
});

export const CMSInlineTextImageSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSInlineTextImageSchema.extend({
    image: InlineTextImageSchema,
    rich_text: z.string(),
    image_position: ImagePositionSchema,
  });

export type CMSInlineTextImage = z.infer<
  typeof BaseCMSInlineTextImageSchema
> & {
  image: z.infer<typeof InlineTextImageSchema>;
  image_position: z.infer<typeof ImagePositionSchema>;
  rich_text: string;
};

/**
 * Represents a Zod schema for `CMSPlaceholder component`
 **/

// TODO: Add more enum if there are any component arises.
const CMSPlaceholderNameEnum = z.enum([
  'mobile_coverage_map',
  'app_links',
  'app_links_black',
  'app_links_centre',
  'app_links_center',
  'app_links_centre_black',
  'app_links_center_black',
  'social_links',
  'download_app',
  'download_app_black',
  'download_app_centre',
  'download_app_center',
  'download_app_centre_black',
  'download_app_center_black',
  'app_comparison_table',
  'exetel_logo',
  'exetel_logo_green',
  'speed_test',
  'spacer',
]);
const CMSPlaceholderNameSection = z.string().startsWith('section_link#');

const CMSPlaceholderNameSchema = z.union([
  CMSPlaceholderNameEnum,
  CMSPlaceholderNameSection,
]);

export const CMSPlaceholderSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSPlaceholderSchema.extend({
    name: CMSPlaceholderNameSchema,
  });

export type CMSPlaceholder = z.infer<typeof BaseCMSPlaceholderSchema> & {
  name: z.infer<typeof CMSPlaceholderNameEnum> | `section_link#${string}`;
};

/**
 * Represents a Zod schema for `CMSPlaceholder component`
 **/

const FormSchema = z.object({
  handle: z.string(), //FIXME: should we change this to enum?
});

export const CMSFormSchema: z.ZodDiscriminatedUnionOption<'type'> =
  BaseCMSFormSchema.extend({
    form: FormSchema,
  });

export type CMSForm = z.infer<typeof BaseCMSFormSchema> & {
  form: z.infer<typeof FormSchema>;
};

/**
 * Represents a Zod schema for all possible `CMSNestedComponents`
 **/
export const CMSNestedComponentSchema = z.discriminatedUnion('type', [
  CMSCardSchema,
  CMSColumnsSchema,
  CMSTextSchema,
  CMSTextAdvancedSchema,
  CMSAddressSearchSchema,
  CMSPlanCardsSchema,
  CMSContentSchema,
  CMSTrustpilotWidgetSchema,
  CMSTrustpilotCarouselSchema,
  CMSFaqsPartialSchema,
  CMSAverageActivationSchema,
  CMSRichTextSchema,
  CMSArticleCardSchema,
  CMSCallToActionSchema,
  CMSImageSchema,
  CMSVideoSchema,
  CMSCTASchema,
  CMSQueueCountSchema,
  CMSInlineTextImageSchema,
  CMSPlaceholderSchema,
  CMSFormSchema,
  CMSTabsSchema,
]);

/**
 * Represents components that can be nested within other `CMS components`.
 * These components are specifically designed to be used as children or nested content
 * in parent CMS components like columns, tabs, or sections.
 *
 * @example
 * // Can be used inside a CMSColumn
 * type ColumnContent = {
 *   items: NestedCMSComponent[];
 * }
 */
export type CMSNestedComponent =
  | CMSCard
  | CMSColumn
  | CMSText
  | CMSTextAdvanced
  | CMSAddressSearch
  | CMSPlanCards
  | CMSContent
  | CMSTrustpilotWidget
  | CMSTrustpilotCarousel
  | CMSFaqsPartial
  | CMSAverageActivation
  | CMSRichText
  | CMSArticleCard
  | CMSCallToAction
  | CMSImage
  | CMSVideo
  | CMSCTA
  | CMSQueueCount
  | CMSInlineTextImage
  | CMSPlaceholder
  | CMSForm
  | CMSTabs;

/**
 * Represents a Zod schema for all possible `CMS components`
 **/
export const CMSComponentSchema = z.discriminatedUnion('type', [
  CMSCardSchema,
  CMSColumnsSchema,
  CMSBannerSchema,
  CMSTabsSchema,
  CMSTextSchema,
  CMSTextAdvancedSchema,
  CMSPlanCardsSchema,
  CMSArticleCardSchema,
  CMSCallToActionSchema,
  CMSImageSchema,
  CMSVideoSchema,
  CMSCTASchema,
  CMSQueueCountSchema,
  CMSInlineTextImageSchema,
  CMSPlaceholderSchema,
  CMSFormSchema,
]);

/**
 * Represents all available top-level CMS components.
 * These components can be used directly in page layouts and may contain
 * nested components within their structure.
 *
 * @example
 * // Used in page content
 * type PageContent = {
 *   content: CMSComponent[];
 * }
 */
export type CMSComponent =
  | CMSCard
  | CMSColumn
  | CMSBanner
  | CMSTabs
  | CMSText
  | CMSTextAdvanced
  | CMSPlanCards
  | CMSArticleCard
  | CMSCallToAction
  | CMSImage
  | CMSVideo
  | CMSCTA
  | CMSQueueCount
  | CMSInlineTextImage
  | CMSPlaceholder
  | CMSForm;
